import { datadogLogs } from '@datadog/browser-logs'
import {
  REACT_APP_APP_NAME,
  REACT_APP_DATADOG_CLIENT_TOKEN,
  REACT_APP_DATADOG_SITE,
  REACT_APP_SENTRY_DSN,
  REACT_APP_VERSION
} from 'Services/config'
import * as Sentry from '@sentry/react'
import { captureMessage } from '@sentry/react'
import { browserTracingIntegration } from '@sentry/browser'
import {
  sanitizeUrl,
  sanitizeObject,
  sanitizeTags,
  sanitizeBreadcrumbData
} from 'Utils/helpers/sanitize'

export const initializeLogging = () => {
  if (REACT_APP_SENTRY_DSN) {
    Sentry.init({
      dsn: REACT_APP_SENTRY_DSN,
      integrations: [browserTracingIntegration()],
      tracesSampleRate: 1.0,
      // Sanitize error/exception events
      beforeSend(event) {
        if (event.request?.url) {
          event.request.url = sanitizeUrl(event.request.url)
        }
        if (event.request?.query_string) {
          delete event.request.query_string
        }

        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map((crumb) => {
            if (crumb.data) {
              crumb.data = sanitizeBreadcrumbData(crumb.data)
            }
            return crumb
          })
        }

        if (event.contexts) {
          event.contexts = sanitizeObject(event.contexts)
        }
        if (event.extra) {
          event.extra = sanitizeObject(event.extra)
        }

        return event
      },
      // Sanitize transactions/performance data
      beforeSendTransaction(event) {
        if (event.request?.url) {
          event.request.url = sanitizeUrl(event.request.url)
        }

        if (typeof event.transaction === 'string') {
          const sanitized = sanitizeUrl(event.transaction)
          if (sanitized) {
            event.transaction = sanitized
          }
        }

        if (event.tags) {
          event.tags = sanitizeTags(event.tags) ?? event.tags
        }

        if (event.spans) {
          event.spans = event.spans.map((span) => {
            if (span.data) {
              span.data = sanitizeObject(span.data)
            }
            return span
          })
        }

        if (event.contexts) {
          event.contexts = sanitizeObject(event.contexts)
        }

        return event
      },
      // Sanitize breadcrumbs
      beforeBreadcrumb(breadcrumb) {
        if (breadcrumb.data) {
          breadcrumb.data = sanitizeBreadcrumbData(breadcrumb.data)
        }
        return breadcrumb
      }
    })
  }
  if (REACT_APP_DATADOG_CLIENT_TOKEN) {
    datadogLogs.init({
      clientToken: REACT_APP_DATADOG_CLIENT_TOKEN,
      site: REACT_APP_DATADOG_SITE,
      service: REACT_APP_APP_NAME,
      forwardErrorsToLogs: true,
      useSecureSessionCookie: true,
      sampleRate: 100,
      beforeSend: (log) => {
        // Even if running DD locally no need for webpack-dev-server errors
        if (log.message.includes('webpack-dev-server')) {
          return false
        }
      }
    })
  }
}

export const setLogContextUser = ({ id, employerId }: { id: number; employerId?: number }) => {
  if (REACT_APP_SENTRY_DSN) {
    Sentry.setUser({ id: id.toString(), employerId: employerId, release: REACT_APP_VERSION })
  }
  if (REACT_APP_DATADOG_CLIENT_TOKEN) {
    datadogLogs.setUser({ id: id.toString() })
    datadogLogs.setUserProperty('release', REACT_APP_VERSION)

    if (employerId) {
      datadogLogs.setUserProperty('employer', employerId.toString())
    }
  }
}

export enum LogLevel {
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
  DEBUG = 'debug'
}
type LogContext = { level?: LogLevel; extra?: Record<string, unknown> }

export const logMessage = (message: string, context?: LogContext | LogLevel) => {
  const level = (context && (typeof context === 'string' ? context : context.level)) || 'error'
  const extra = context && (typeof context === 'string' ? {} : context.extra)

  if (REACT_APP_SENTRY_DSN) {
    captureMessage(message, { level, extra })
  }
  if (REACT_APP_DATADOG_CLIENT_TOKEN) {
    ;({
      error: datadogLogs.logger.error.bind(datadogLogs.logger),
      warning: datadogLogs.logger.warn.bind(datadogLogs.logger),
      info: datadogLogs.logger.info.bind(datadogLogs.logger),
      debug: datadogLogs.logger.debug.bind(datadogLogs.logger)
    })[level](message, extra)
  }
}
